import '../render/shapes';

import { compareFloorPlanDrawItemsPaintOrder } from '../geometry/floor-plan-draw-item-order';
import { createFloorPlanVertexNode } from '../render/vertex';
import {
  addFloorPlanHitRect,
  floorPlanInnerStrokeAttrs,
  FLOOR_PLAN_SHAPE_KEY_FALLBACK_RECT,
  getFloorPlanShapePainter,
  type FloorPlanShapePaintContext,
  resolveFloorPlanShapePaintKey,
} from '../render/paint';
import { paintBarStoolsOnGroup } from '../render/bar';
import { createFloorPlanTableNode } from '../render/table';
import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import {
  UppFloorPlanArchitecture,
  UppFloorPlanBar,
  UppFloorPlanFurniture,
  UppFloorPlanVertex,
  UppFloorPlanTable,
} from '../plan/floor-plan-element';
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';

function paintGroupedFloorPlanElement(
  K: any,
  group: any,
  widthPx: number,
  heightPx: number,
  scalePxPerCm: number,
  theme: UppFloorPlanTheme,
  el: UppFloorPlanElement,
  innerStrokeUsesSelection: boolean,
  selected: boolean,
  listening: boolean,
  editSelected: boolean,
): void {
  const key = resolveFloorPlanShapePaintKey(el);
  const painter =
    getFloorPlanShapePainter(key) ?? getFloorPlanShapePainter(FLOOR_PLAN_SHAPE_KEY_FALLBACK_RECT);
  const ctx: FloorPlanShapePaintContext = {
    K,
    group,
    widthPx,
    heightPx,
    scalePxPerCm,
    theme,
    innerStrokeUsesSelection,
    element: el,
    elementPointerListening: listening,
  };
  painter?.(ctx);
  addFloorPlanHitRect(K, group, widthPx, heightPx, theme, selected, listening, 0, 0, editSelected);
  if (el instanceof UppFloorPlanBar) {
    paintBarStoolsOnGroup(ctx);
  }
  group.listening(listening);
}

/**
 * Builds a Konva node for a floor-plan element (cm → px using `scalePxPerCm`).
 * Tables return a {@link Konva.Rect}; wall-chain vertex markers return a {@link Konva.Circle}; grouped items return a {@link Konva.Group}
 * with a hit rectangle on top.
 */
export function createFloorPlanElementNode(
  K: any,
  el: UppFloorPlanElement,
  scalePxPerCm: number,
  theme: UppFloorPlanTheme,
  selected: boolean,
  listening: boolean,
  edit = false,
): any {
  const x = el.xCm * scalePxPerCm;
  const y = el.yCm * scalePxPerCm;
  const w = el.widthCm * scalePxPerCm;
  const h = el.heightCm * scalePxPerCm;
  const rot = el.rotationDeg;
  const editSelected = edit && selected;

  if (el instanceof UppFloorPlanVertex) {
    return createFloorPlanVertexNode(K, el, scalePxPerCm, theme, selected, listening, edit);
  }

  if (el instanceof UppFloorPlanTable) {
    return createFloorPlanTableNode(
      K,
      el,
      x,
      y,
      w,
      h,
      rot,
      scalePxPerCm,
      theme,
      selected,
      listening,
      editSelected,
    );
  }

  const g = new K.Group({
    id: el.uuid,
    x,
    y,
    rotation: rot,
    width: w,
    height: h,
  });

  if (el instanceof UppFloorPlanBar) {
    paintGroupedFloorPlanElement(K, g, w, h, scalePxPerCm, theme, el, false, selected, listening, editSelected);
    return g;
  }

  if (el instanceof UppFloorPlanArchitecture) {
    paintGroupedFloorPlanElement(K, g, w, h, scalePxPerCm, theme, el, false, selected, listening, editSelected);
    return g;
  }

  if (el instanceof UppFloorPlanFurniture) {
    paintGroupedFloorPlanElement(K, g, w, h, scalePxPerCm, theme, el, editSelected, selected, listening, editSelected);
    return g;
  }

  const stroke =
    editSelected && selected
      ? { stroke: '#dc2626' as const, strokeWidth: theme.roomStrokeWidth + 1.5 }
      : floorPlanInnerStrokeAttrs(theme, false);
  return new K.Rect({
    id: el.uuid,
    x,
    y,
    width: w,
    height: h,
    fill: theme.elementFill,
    ...stroke,
    rotation: rot,
    listening,
  });
}

/**
 * Paints elements by ascending {@link UppFloorPlanElement.zIndex} (lower = underneath).
 * When `zIndex` ties, legacy stacking applies: **vertex markers** (wall chain) first (bottom), then **tables**, then **draw pieces** (v3 type order).
 * Interior wall segments (open polyline) are drawn on the canvas after the room rectangle; they do not replace the room bounds.
 */
export function buildFloorPlanPaintSequence(elements: readonly UppFloorPlanElement[]): UppFloorPlanElement[] {
  const byIndex = new Map<UppFloorPlanElement, number>();
  elements.forEach((e, i) => byIndex.set(e, i));

  const legacyRank = (el: UppFloorPlanElement): number => {
    if (el instanceof UppFloorPlanVertex) {
      return 0;
    }
    if (el instanceof UppFloorPlanTable) {
      return 1;
    }
    return 2;
  };

  return [...elements].sort((a, b) => {
    if (a.zIndex !== b.zIndex) {
      return a.zIndex - b.zIndex;
    }
    const ra = legacyRank(a);
    const rb = legacyRank(b);
    if (ra !== rb) {
      return ra - rb;
    }
    if (a instanceof UppFloorPlanTable && b instanceof UppFloorPlanTable) {
      return (byIndex.get(a) ?? 0) - (byIndex.get(b) ?? 0);
    }
    if (a instanceof UppFloorPlanVertex && b instanceof UppFloorPlanVertex) {
      return (byIndex.get(a) ?? 0) - (byIndex.get(b) ?? 0);
    }
    const z = compareFloorPlanDrawItemsPaintOrder(a, b);
    if (z !== 0) {
      return z;
    }
    return (byIndex.get(a) ?? 0) - (byIndex.get(b) ?? 0);
  });
}
